import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {NewTask, Task, TaskComment} from './task-management.interface';
import {FileUploadService} from '../../file-upload.service';
import {ProductsForCustomerTicketUploadComponent} from './actions/products-for-customer-ticket-upload/products-for-customer-ticket-upload.component';
import {DialogService} from '../../shared/services/dialog.service';
import {ReviewEventCancellationReasonComponent} from './actions/review-event-cancellation-reason/review-event-cancellation-reason.component';

const _MS_PER_DAY = 24 * 60 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
@Injectable()
export class TaskManagementApiService {
  constructor(
    private http: HttpClient,
    private fileUploader: FileUploadService,
    private dialogService: DialogService,
  ) {}

  getTaskList(filters, page = 1) {
    const params = {
      page: page.toString(),
    };
    Object.entries(filters).forEach(([key, val]) => {
      if (typeof val === 'boolean' || val === 0 || val) {
        params[key] = val;
      }
    });
    return this.http.get(`/rest-api/v1/admin/tasks`, {params});
  }
  getTaskStatuses = () => this.http.get(`rest-api/v2/companies/0/tasks/statuses`);
  getTaskDetails = (task: Task) => this.http.get(`/rest-api/v1/admin/tasks/${task.id}`);

  getPredefinedTaskVerbs = (eventId = null) =>
    this.http.get(`/rest-api/v1/admin/tasks/get_predefined_tasks_verbs`, {
      params: {event_id: eventId},
    });

  createTask = (task: NewTask) => this.http.post<Task>('/rest-api/v1/admin/tasks', task);

  createTasks = data => this.http.post('/rest-api/v1/admin/tasks/create_tasks', data);

  assignTask = (task: Task, user) =>
    this.http.post<Task>(`/rest-api/v1/admin/tasks/${task.id}/assign`, {
      user: user.id,
    });

  changeDeadline = (task: Task, date: string) =>
    this.http.post(`/rest-api/v1/admin/tasks/${task.id}/deadline`, {
      date: date,
    });

  updateTasks = (tasks: Array<number>, data) =>
    this.http.post(`/rest-api/v1/admin/tasks/update_tasks`, {
      tasks: tasks,
      data: data,
    });

  changeStatus = (task: Task, newStatus: number) =>
    this.http.post(`/rest-api/v1/admin/tasks/${task.id}/status`, {
      status: newStatus,
    });

  addComment = (task: Task, comment: TaskComment) =>
    this.http.post(`/rest-api/v1/admin/tasks/${task.id}/comments`, comment);

  cancellComment = (task_id: number, comment_id) =>
    this.http.post(`/rest-api/v1/admin/tasks/${task_id}/comments/cancell`, {
      comment_id: comment_id,
    });

  taskAction = (task: Task, detail?: string, payload?: object) =>
    this.http.post(`/rest-api/v1/admin/tasks/${task.id}/action`, {
      action_detail: detail,
      products: payload,
    });

  productsTaskAction = (taskId, payload?: object) =>
    this.http.post(`/rest-api/v1/admin/tasks/${taskId}/products_action`, {
      products: payload,
    });

  relatedEventDays = taskId => this.http.get(`/rest-api/v1/admin/tasks/${taskId}/get_event_days`);

  getCustomerProducts = campaignId => this.http.get(`/rest-api/v1/admin/campaigns/${campaignId}/customer_products`);

  getEventComments = (eventId: number) => this.http.get(`/rest-api/v1/admin/event/${eventId}/comments`);
  cancelEventComment = (eventId: number, commentId: number, userId: number) =>
    this.http.put(`/rest-api/v1/admin/event/${eventId}/comments/${commentId}/mark_as_cancelled`, {
      comment_id: commentId,
      user: userId,
    });

  addEventComment = (eventId: number, comment: any) =>
    this.http.post(`/rest-api/v1/admin/event/${eventId}/comments`, comment);

  uploadAttachments = (task: Task, files: Set<File>) =>
    this.fileUploader.upload(files, `/rest-api/v1/admin/tasks/${task.id}/attachments`);

  getDataMaximisationTasks = () => this.http.get(`/rest-api/v1/admin/tasks/get_data_maximistaion_tasks`);
  downloadPFBanners = taskId =>
    this.http.get(`/rest-api/v1/admin/tasks/${taskId}/get_partyflock_banners`, {
      responseType: 'blob',
      observe: 'response',
    });
  unlockBanners = taskId => this.http.post(`/rest-api/v1/admin/tasks/${taskId}/unlock_partyflock_banners`, {});
  changeVisibilityInCD = taskId =>
    this.http.patch(`/rest-api/v1/admin/tasks/${taskId}/change_visibility_in_company_dashboard`, {});

  getCSVTasks = filters => {
    const params = {};
    Object.entries(filters).forEach(([key, val]) => {
      if (typeof val === 'boolean' || val === 0 || val) {
        params[key] = val;
      }
    });
    return this.http.get('/rest-api/v1/admin/tasks/csv', {
      params,
      responseType: 'text',
    });
  };

  getTaskScanResults = taskId => this.http.get(`/rest-api/v1/admin/tasks/${taskId}/get_scan_results`);
  uploadScanResults = (companyId: number, taskId: number, payload: object) =>
    this.http.post(`rest-api/v2/companies/${companyId}/tasks/${taskId}/upload_scan_results`, payload);
  getTicketProviders = (companyId: number) =>
    this.http.get(`rest-api/v1/companies/${companyId}/shop/product/ticket_providers_scan_results`);

  deleteScanResults = (companyId: number, taskId: number, payload: object, params?) =>
    this.http.delete(`rest-api/v2/companies/${companyId}/tasks/${taskId}/delete_scan_results`, {
      body: payload,
      params: params,
    });
  connectWingameToTask = (taskId: number, wingameId: number) =>
    this.http.post(`/rest-api/v1/admin/tasks/${taskId}/connect_wingame`, {wingame_id: wingameId});

  runPredefinedAction(task) {
    let modalClass = null;
    let data = {task: task};
    const actionSystemName = task.predefined_action.system_name;

    if (actionSystemName === 'create_cancellation_mail') {
      modalClass = ReviewEventCancellationReasonComponent;
    }

    if (actionSystemName === 'products_for_customer_ticket_upload') {
      modalClass = ProductsForCustomerTicketUploadComponent;
    }

    if (modalClass) {
      const dialogRef = this.dialogService.openDialog(modalClass, {
        width: '1000px',
        data: data,
        panelClass: ['cdk-mobile-full-screen', 'cdk-event-sponsors-update'],
      });

      dialogRef.afterClosed().subscribe(result => {
        return result;
      });
    }
    return {success: '', message: '', redirect: ''};
  }

  taskColor(task) {
    if (task.task_system_type === 'set_campaign') {
      return 'bg-diamond';
    }
    if (task.status === 5) {
      // in review
      return 'yellow';
    }

    if (task.status === 1 || task.status === 2) {
      // task status == done or cancelled
      return 'bg-neutral';
    } else if (task.status === 3) {
      // task status == sent
      return 'bg-yellow';
    } else {
      const daysToEnd = (Date.parse(task.deadline) - Date.now()) / _MS_PER_DAY;
      if (daysToEnd <= 0) {
        return 'bg-red';
      }
      if (daysToEnd > 0 && daysToEnd <= 2) {
        return 'bg-yellow';
      }
      if (daysToEnd > 2 && daysToEnd < 7) {
        return 'bg-green';
      }
    }

    return 'bg-neutral';
  }

  createProduct(task) {
    const dialogRef = this.dialogService.openDialog(ProductsForCustomerTicketUploadComponent, {
      width: '1200px',
      data: {task: task},
      panelClass: ['cdk-mobile-full-screen', 'cdk-event-sponsors-update'],
    });
    let modalData;
    dialogRef.afterClosed().subscribe(data => {
      modalData = data;
    });
    return modalData;
  }

  isActionAvailable(task, url) {
    if (task.predefined_action?.already_called) {
      return false;
    }
    if (url) {
      return task.predefined_action && task.predefined_action?.url && !task.is_company_task;
    } else {
      return task.predefined_action && !task.predefined_action?.url && !task.is_company_task;
    }
  }

  downloadBanners(task, onComplete?: () => void) {
    this.downloadPFBanners(task.id).subscribe(
      response => {
        const blob = new Blob([response.body!], {type: 'application/zip'});
        const url = window.URL.createObjectURL(blob);
        const eventName =
          task.related_event.name.length <= 40 ? task.related_event.name : task.related_event.name.slice(0, 40);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${task.related_event.id}-${eventName}-banners.zip`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      () => {},
      () => {
        if (onComplete) onComplete();
      },
    );
  }
  getRelatedObjectDetailsUrl(task) {
    // Django content types
    // (39, event);
    // (127, product);
    // (108, wingame);

    const object_type = task.related_content_type;
    const object_id = task.related_object_id;

    switch (object_type) {
      // case 'event':
      //   return `/admin/events/${object_id}/details`;
      case 127:
        // admin/shop/edit/products/6658/details
        return `/admin/shop/edit/products/${object_id}/details`;
      case 108:
        // admin/wingames/edit/8436/details;
        return `/admin/wingames/edit/${object_id}/details`;
      default:
        return '';
    }
  }
}
